import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  X,
  ChevronUp,
  ChevronDown,
  ExternalLink,
  Heart,
  Linkedin,
  FileText,
  Briefcase,
  Loader,
  Loader2,
} from "lucide-react";
import {
  generateSignedUrl,
  requestInterview,
} from "../../../redux/actions/halloffame";
import Spinner from "../../../components/Spinner";
import { toast } from "react-toastify";
import { Mixpanel } from "../../../config/Mixpanel";
import Constants from "../../../common/constants";
import { saveAs } from "file-saver";

const ProfileModal = ({
  fullProfile,
  matchScore,
  setIsProfileModalOpen,
  isFetchingFullProfile,
  isWishlistedLocal,
  addWishlistedProfile,
  removeWishlistedProfile,
}) => {
  const dispatch = useDispatch();
  const recruiterAccessToken = useSelector(
    (store) => store?.hallOfFame?.recruiterAccessToken,
  );
  const [isInterviewRequestSent, setIsInterviewRequestSent] = useState(false);
  const [interviewRequested, setInterviewRequested] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Accordion states
  const [openSections, setOpenSections] = useState({
    experience: true,
    education: true,
    certifications: false,
    projects: false,
    skills: false,
    accomplishments: false,
  });

  const [isBioExpanded, setIsBioExpanded] = useState(false);

  if (isFetchingFullProfile) return <Spinner />;

  if (!fullProfile) return null;

  const {
    profile,
    experience = [],
    education = [],
    certifications = [],
    projects = [],
    skills = {},
    accomplishments = {},
    gs_uri = "",
  } = fullProfile;

  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const formatDuration = (monthsCount) => {
    if (!monthsCount) return "N/A";
    const years = Math.floor(monthsCount / 12);
    const months = monthsCount % 12;

    const parts = [];
    if (years > 0) parts.push(`${years} ${years === 1 ? "year" : "years"}`);
    if (months > 0)
      parts.push(`${months} ${months === 1 ? "month" : "months"}`);

    return parts.length > 0 ? parts.join(" ") : "N/A";
  };

  const downloadResume = (e) => {
    e?.stopPropagation();
    setIsDownloading(true);
    Mixpanel.track(Constants.Mixpanel.RECRUITER_DOWNLOAD_CV_CLICKED);
    dispatch(
      generateSignedUrl(
        gs_uri,
        recruiterAccessToken,
        (url) => {
          // Create a safe filename from the user's name
          const safeName = (profile?.full_name || "candidate")
            .replace(/[^a-z0-9]/gi, "_")
            .toLowerCase();
          const filename = `${safeName}_resume.pdf`;

          fetch(url)
            .then((response) => {
              if (!response.ok) throw new Error("Network response was not ok");
              return response.blob();
            })
            .then((blob) => {
              saveAs(blob, filename);
              toast.success("Resume downloaded successfully");
            })
            .catch((error) => {
              console.error("Download error:", error);
              toast.error("Failed to download resume file");
              // Fallback to opening in new tab if blob download fails
              window.open(url, "_blank");
            });
          setIsDownloading(false);
        },
        (error) => {
          toast.error("Failed to retrieve resume");
          setIsDownloading(false);
        },
      ),
    );
  };

  const requestInteviewHandler = (e) => {
    e?.stopPropagation();
    Mixpanel.track(Constants.Mixpanel.RECRUITER_REQUEST_INTERVIEW_CLICKED);
    dispatch(
      requestInterview(
        profile?.email,
        recruiterAccessToken,
        (successObj) => {
          toast.success("Interview request sent successfully");
          setIsInterviewRequestSent(true);
          setInterviewRequested(true);
        },
        (errorObj) => {
          toast.error("Interview already requested");
        },
      ),
    );
  };

  const renderAccomplishmentItem = (item) => {
    let data = item;

    // Handle JSON strings
    if (typeof item === "string") {
      try {
        if (item.trim().startsWith("{")) {
          data = JSON.parse(item);
        }
      } catch (e) {
        // Parsing error, keep as string
      }
    }

    // Return simple string if it's still a string
    if (typeof data === "string") {
      return <span>{data}</span>;
    }

    // Render object properties
    const name = data.award_name || data.title || data.name;
    const organization =
      data.issuing_organization || data.organization || data.publisher;
    const date = data.date || data.publication_date || data.year;
    const description = data.description || data.summary;
    const type = data.award_type || data.type;

    return (
      <div className="flex flex-col gap-1">
        <div className="text-blue-200 font-medium">
          {name}
          {organization && (
            <span className="font-normal text-gray-400"> • {organization}</span>
          )}
        </div>
        {description && (
          <p className="text-xs leading-relaxed text-gray-400">{description}</p>
        )}
        <div className="flex gap-2 text-xs text-gray-500">
          {type && <span>{type}</span>}
          {date && <span>{date}</span>}
        </div>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-[#0a0f1a]/90 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="animate-in slide-in-from-bottom sm:zoom-in-95 relative flex h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-t-[20px] rounded-b-none border border-[rgba(255,255,255,0.06)] bg-[#111827] font-sans text-[#f0f2f5] shadow-2xl duration-200 sm:h-[90vh] sm:rounded-[20px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsProfileModalOpen(false);
          }}
          className="absolute right-4 top-4 z-10 flex h-[30px] w-[30px] items-center justify-center rounded-full border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.05)] text-[#8b95a8] transition-all hover:bg-[#1e2a3e] hover:text-[#f0f2f5]"
        >
          <X size={14} />
        </button>

        {/* Header Section */}
        <div className="flex-none px-5 pt-4 pb-2">
          {/* Name */}
          <div className="pr-10 pt-4">
            <h2 className="font-sans text-[21px] font-bold tracking-tight text-[#f0f2f5]">
              {profile?.full_name || "Unknown Name"}
            </h2>
          </div>

          {/* Contact Row */}
          <div className="mt-1 flex items-center gap-1.5 text-[12.5px] text-[#8b95a8]">
            {profile?.email && (
              <a
                href={`mailto:${profile.email}`}
                className="transition-colors hover:text-[#f0f2f5]"
              >
                {profile.email}
              </a>
            )}
            {profile?.email && profile?.current_location && (
              <span className="h-[3px] w-[3px] rounded-full bg-[#5a6477]"></span>
            )}
            {profile?.current_location && (
              <span>
                {profile.current_location.city},{" "}
                {profile.current_location.country}
              </span>
            )}
          </div>

          {/* Action Row */}
          <div className="mt-3.5 mb-3.5 flex flex-wrap items-center gap-2">
            {/* Match Score Pill - Omitting as per data unavailability, but preserving layout space if needed */}

            {matchScore && (
              <div className="mr-auto flex items-center gap-1.5 rounded-[20px] border border-[rgba(212,168,67,0.22)] bg-[rgba(212,168,67,0.1)] px-2.5 py-1.5">
                <span className="bg-gradient-to-br from-[#d4a843] to-[#e8c468] bg-clip-text font-sans text-[16px] font-extrabold text-transparent">
                  {matchScore}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-[#c49a3a]">
                  Match
                </span>
              </div>
            )}

            {profile?.linkedin_url && (
              <a
                href={profile.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded-[8px] border border-[rgba(74,142,255,0.22)] bg-[rgba(74,142,255,0.12)] px-2 py-1.5 text-[12.5px] font-semibold text-[#4a8eff] transition-all hover:border-[rgba(74,142,255,0.35)] hover:bg-[rgba(74,142,255,0.2)]"
                title="LinkedIn Profile"
              >
                <Linkedin size={15} fill="currentColor" />
              </a>
            )}

            {profile?.portfolio_url && (
              <a
                href={profile.portfolio_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded-[8px] border border-[rgba(74,142,255,0.22)] bg-[rgba(74,142,255,0.12)] px-3 py-1.5 text-[12.5px] font-semibold text-[#4a8eff] transition-all hover:border-[rgba(74,142,255,0.35)] hover:bg-[rgba(74,142,255,0.2)]"
              >
                <Briefcase size={14} />
                Portfolio
              </a>
            )}

            {gs_uri && (
              <button
                onClick={downloadResume}
                disabled={isInterviewRequestSent}
                className="flex items-center gap-1.5 rounded-[8px] border border-[rgba(255,255,255,0.1)] bg-[#1e2a3e] px-3 py-1.5 text-[12.5px] font-semibold text-[#f0f2f5] transition-all hover:border-[rgba(255,255,255,0.15)] hover:bg-[#253048] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FileText size={14} />
                {isDownloading ? (
                  <Loader2 className="animate-spin" size={14} />
                ) : (
                  "Download CV"
                )}
              </button>
            )}

            {profile?.github_url && (
              <a
                href={profile.github_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded-[8px] border border-[rgba(255,255,255,0.1)] bg-[#1e2a3e] px-3 py-1.5 text-[12.5px] font-semibold text-[#f0f2f5] transition-all hover:border-[rgba(255,255,255,0.15)] hover:bg-[#253048]"
              >
                <img
                  src="https://img.learntube.ai/public-assets/company-logos/github-logo.webp"
                  alt="GitHub"
                  className="h-3.5 w-3.5 rounded-full bg-white"
                />
                GitHub
              </a>
            )}
          </div>

          <div className="h-[1px] w-full bg-[rgba(255,255,255,0.06)]"></div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-5 py-2 scrollbar-hide">
          {/* Bio */}
          {profile?.summary_text && (
            <div className="mb-6 pt-2">
              <h3 className="mb-2 text-[15px] font-bold text-[#f0f2f5]">
                About
              </h3>
              <p
                className={`text-[13px] leading-relaxed text-[#8b95a8] ${
                  !isBioExpanded ? "line-clamp-3" : ""
                }`}
              >
                {profile.summary_text}
              </p>
              <button
                onClick={() => setIsBioExpanded(!isBioExpanded)}
                className="mt-1 text-[13px] font-medium text-[#4a8eff] hover:text-[#6366f1]"
              >
                {isBioExpanded ? "Read Less" : "Read More"}
              </button>
            </div>
          )}

          {/* Key Strengths */}
          {profile?.key_strengths && profile.key_strengths.length > 0 && (
            <div className="mb-6">
              <h3 className="mb-3 text-[15px] font-bold text-[#f0f2f5]">
                Key Strengths
              </h3>
              <div className="flex flex-wrap gap-2">
                {profile.key_strengths.map((strength, idx) => (
                  <span
                    key={idx}
                    className="rounded-md border border-[rgba(74,142,255,0.12)] bg-[#1a2236] px-3 py-1.5 text-[13px] text-[#f0f2f5]"
                  >
                    {strength}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 1. Experience Accordion */}
          {experience.length > 0 && (
            <SectionAccordion
              title="Experience"
              isOpen={openSections.experience}
              onToggle={() => toggleSection("experience")}
            >
              <div className="relative">
                {experience.map((exp, idx) => (
                  <div key={idx} className="relative flex gap-3 pb-5 last:pb-0">
                    {/* Connecting Line */}
                    {idx !== experience.length - 1 && (
                      <div className="absolute left-[7px] top-[22px] bottom-0 w-[2px] bg-[rgba(255,255,255,0.1)]"></div>
                    )}

                    {/* Dot */}
                    <div className="relative z-10 mt-[3px] h-4 w-4 shrink-0 rounded-full bg-[#4a8eff]"></div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="mb-[1px] text-[14.5px] font-bold text-[#f0f2f5]">
                        {exp.job_title}
                      </div>
                      <div className="mb-[2px] text-[13px] text-[#8b95a8]">
                        {exp.company_name}
                      </div>
                      <div className="mb-1.5 text-[11.5px] text-[#5a6477]">
                        {exp.start_date || "N/A"} - {exp.end_date || "Present"}{" "}
                        · {formatDuration(exp.duration_months)}
                      </div>
                      <p className="text-[12.5px] leading-relaxed text-[#8b95a8]">
                        {exp.raw_description}
                      </p>

                      {exp.key_achievements && exp.key_achievements.length > 0 && (
                        <ul className="mt-1 list-disc pl-3 text-[12.5px] leading-relaxed text-[#8b95a8]">
                          {exp.key_achievements.map((ach, i) => (
                            <li key={i} className="pl-1">
                              {ach}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </SectionAccordion>
          )}

          {/* 2. Education Accordion */}
          {education.length > 0 && (
            <SectionAccordion
              title="Education"
              isOpen={openSections.education}
              onToggle={() => toggleSection("education")}
            >
              <div className="relative">
                {education.map((edu, idx) => (
                  <div key={idx} className="relative flex gap-3 pb-5 last:pb-0">
                    {/* Connecting Line */}
                    {idx !== education.length - 1 && (
                      <div className="absolute left-[7px] top-[22px] bottom-0 w-[2px] bg-[rgba(255,255,255,0.1)]"></div>
                    )}

                    {/* Dot */}
                    <div className="relative z-10 mt-[3px] h-4 w-4 shrink-0 rounded-full bg-[#4a8eff]"></div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="mb-[1px] text-[14.5px] font-bold text-[#f0f2f5]">
                        {edu.institution_name}
                      </div>
                      <div className="mb-[2px] text-[13px] text-[#8b95a8]">
                        {edu.degree_name}{" "}
                        {edu.field_of_study ? `- ${edu.field_of_study}` : ""}
                      </div>
                      <div className="mb-1.5 text-[11.5px] text-[#5a6477]">
                        {edu.start_date} - {edu.end_date}
                      </div>
                      {edu.gpa && (
                        <div className="text-[12.5px] text-[#8b95a8]">
                          GPA: {edu.gpa}/{edu.gpa_scale}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </SectionAccordion>
          )}

          {/* 3. Certifications Accordion */}
          {certifications.length > 0 && (
            <SectionAccordion
              title="Certifications"
              isOpen={openSections.certifications}
              onToggle={() => toggleSection("certifications")}
            >
              <div className="space-y-4">
                {certifications.map((cert, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border border-[rgba(255,255,255,0.1)] bg-[#1a2236] p-4"
                  >
                    <h4 className="font-bold text-[#f0f2f5]">
                      {cert.certification_name}
                    </h4>
                    <div className="mt-1 flex items-center justify-between">
                      <span className="text-[13px] text-[#8b95a8]">
                        {cert.issuing_organization === "Unknown"
                          ? ""
                          : cert.issuing_organization}
                      </span>
                      {cert.credential_url && (
                        <a
                          href={cert.credential_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#4a8eff] hover:text-[#6366f1]"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                    <div className="mt-2 flex gap-2">
                      {cert.certification_level &&
                        cert.certification_level !== "Unknown" && (
                          <span className="rounded bg-[rgba(255,255,255,0.05)] px-2 py-1 text-[11px] text-[#8b95a8]">
                            {cert.certification_level}
                          </span>
                        )}
                      {cert.is_active && (
                        <span className="rounded bg-[rgba(52,211,153,0.10)] px-2 py-1 text-[11px] text-[#34d399]">
                          Active
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </SectionAccordion>
          )}

          {/* 4. Projects Accordion */}
          {projects.length > 0 && (
            <SectionAccordion
              title="Projects"
              isOpen={openSections.projects}
              onToggle={() => toggleSection("projects")}
            >
              <div className="space-y-6">
                {projects.map((proj, idx) => (
                  <div
                    key={idx}
                    className="relative border-l-2 border-[rgba(255,255,255,0.1)] pl-4"
                  >
                    <h4 className="text-[15px] font-bold text-[#f0f2f5]">
                      {proj.project_name}
                    </h4>
                    <div className="mb-1 text-[13px] text-[#4a8eff]">
                      {proj.project_type === "Unknown" ? "" : proj.project_type}
                    </div>
                    <p className="mb-3 text-[13px] text-[#8b95a8]">
                      {proj.project_description}
                    </p>

                    {proj.technologies_used &&
                      proj.technologies_used.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-2">
                          {proj.technologies_used.map((tech, i) => (
                            <span
                              key={i}
                              className="rounded border border-[rgba(255,255,255,0.1)] px-1.5 py-0.5 text-[11px] text-[#8b95a8]"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      )}

                    <div className="text-[11px] text-[#5a6477]">
                      Role: {proj.role_in_project}
                    </div>
                  </div>
                ))}
              </div>
            </SectionAccordion>
          )}

          {/* 5. Skills Accordion */}
          {skills && Object.keys(skills).length > 0 && (
            <SectionAccordion
              title="Skills"
              isOpen={openSections.skills}
              onToggle={() => toggleSection("skills")}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {Object.entries(skills).map(([category, items]) => {
                  if (!items || (Array.isArray(items) && items.length === 0))
                    return null;

                  const title = category
                    .split("_")
                    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                    .join(" ");

                  return (
                    <div key={category} className="rounded-xl bg-[#1a2236] p-4">
                      <h5 className="mb-2 text-[13px] font-semibold text-[#4a8eff]">
                        {title}
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {Array.isArray(items)
                          ? items.map((skill, i) => {
                              const name =
                                typeof skill === "string"
                                  ? skill
                                  : skill.skill_name;
                              const level =
                                typeof skill === "object"
                                  ? skill.proficiency_level
                                  : null;
                              return (
                                <span
                                  key={i}
                                  className="rounded border border-[rgba(255,255,255,0.1)] bg-[#111827] px-2 py-1 text-[11px] text-[#8b95a8]"
                                  title={level}
                                >
                                  {name}
                                </span>
                              );
                            })
                          : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </SectionAccordion>
          )}

          {/* 6. Accomplishments Accordion */}
          {accomplishments &&
            Object.keys(accomplishments).some(
              (k) => accomplishments[k]?.length > 0,
            ) && (
              <SectionAccordion
                title="Accomplishments"
                isOpen={openSections.accomplishments}
                onToggle={() => toggleSection("accomplishments")}
              >
                <div className="space-y-4">
                  {Object.entries(accomplishments).map(([category, items]) => {
                    if (!items || items.length === 0) return null;
                    const title = category
                      .split("_")
                      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                      .join(" ");

                    return (
                      <div key={category}>
                        <h5 className="mb-2 text-[13px] font-semibold text-[#4a8eff]">
                          {title}
                        </h5>
                        <ul className="list-disc space-y-3 pl-4 text-[13px] text-[#8b95a8]">
                          {items.map((item, i) => (
                            <li key={i}>{renderAccomplishmentItem(item)}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </SectionAccordion>
            )}
        </div>

        {/* Sticky CTA Footer */}
        <div className="sticky bottom-0 z-20 flex gap-2.5 bg-gradient-to-t from-[#111827] via-[#111827] to-transparent p-5">
          {/* Shortlist Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              isWishlistedLocal
                ? removeWishlistedProfile(profile?.email)
                : addWishlistedProfile(profile?.email);
            }}
            className={`group flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-[14px] font-bold transition-all duration-200 
                            ${
                              isWishlistedLocal
                                ? "border-transparent bg-gradient-to-br from-[#34d399] to-[#6ee7b7] text-[#0a0f1a]"
                                : "border-[rgba(52,211,153,0.28)] bg-[rgba(52,211,153,0.10)] text-[#34d399] hover:border-[rgba(52,211,153,0.42)] hover:bg-[rgba(52,211,153,0.18)]"
                            }`}
          >
            <Heart
              size={18}
              className={`transition-transform duration-200 group-hover:scale-110 ${
                isWishlistedLocal ? "fill-[#0a0f1a]" : ""
              }`}
            />
            {isWishlistedLocal ? "Shortlisted" : "Shortlist"}
          </button>

          {/* Quick Connect Button */}
          <button
            onClick={requestInteviewHandler}
            disabled={isInterviewRequestSent}
            className="relative flex flex-1 flex-col items-center justify-center overflow-hidden rounded-xl border-none bg-gradient-to-br from-[#4a8eff] to-[#6366f1] px-4 py-2.5 shadow-[0_4px_20px_rgba(74,142,255,0.25)] transition-all duration-200 hover:-translate-y-[1px] hover:shadow-[0_6px_28px_rgba(74,142,255,0.35)] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <span className="relative z-10 font-sans text-[15px] font-bold tracking-wide text-white">
              {interviewRequested ? "Interview Requested" : "Quick Connect"}
            </span>
            <span className="relative z-10 mt-[1px] font-sans text-[10.5px] font-medium text-[rgba(255,255,255,0.65)]">
              Email intro · no commitment
            </span>
            {/* Shimmer effect overlay */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[rgba(255,255,255,0.1)] to-transparent"></div>
          </button>
        </div>
      </div>
    </div>
  );
};

// Reusable Accordion Component - Updated Styles
const SectionAccordion = ({ title, isOpen, onToggle, children }) => {
  return (
    <div className="mb-2 overflow-hidden border-b border-[rgba(255,255,255,0.06)] transition-all duration-300 last:border-0">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between py-3.5 transition-colors hover:text-[#f0f2f5]"
      >
        <h3 className="font-sans text-[15px] font-bold text-[#f0f2f5]">
          {title}
        </h3>
        {isOpen ? (
          <ChevronUp size={18} className="text-[#5a6477]" />
        ) : (
          <ChevronDown size={18} className="text-[#5a6477]" />
        )}
      </button>
      {isOpen && (
        <div className="animate-in slide-in-from-top-2 pb-4 pt-1 duration-200">
          {children}
        </div>
      )}
    </div>
  );
};

export default ProfileModal;
